import {
  useNodeTree,
  useNodeTreeState,
  useContextMenu,
  NodeRow,
  Node
} from "@darksnow-ui/node-tree-headless";
import { TreeNode } from "./TreeNode";
import { ContextMenu } from "./ContextMenu";
import { useState, useCallback } from "react";
import { clsx } from "clsx";

export interface NodeTreeProps {
  className?: string;
  style?: React.CSSProperties;
  renderNode?: (props: { row: any }) => React.ReactNode;
  renderEmptyState?: () => React.ReactNode;
  onContextMenuAction?: (action: string, nodeId: string) => void;
  indentSize?: number;
  showContextMenu?: boolean;
  contextMenuClassName?: string;
  emptyStateClassName?: string;
  showRootDropArea?: boolean;
  rootDropAreaClassName?: string;
  renderRootDropArea?: (isDragOver: boolean) => React.ReactNode;
  resolveIcon?: (node: Node, isExpanded: boolean) => string;
}

export function NodeTree({
  className,
  style,
  renderNode,
  renderEmptyState,
  onContextMenuAction,
  indentSize = 20,
  showContextMenu = true,
  contextMenuClassName,
  emptyStateClassName,
  showRootDropArea = true,
  rootDropAreaClassName,
  renderRootDropArea,
  resolveIcon,
}: NodeTreeProps) {
  const context = useNodeTree();
  const indexedNodes = useNodeTreeState((state) => state.indexedNodes);
  const nodes = useNodeTreeState((state) => state.nodes);
  const dragOverId = useNodeTreeState((state) => state.dragOverId);
  const dragStartId = useNodeTreeState((state) => state.dragStartId);
  const { contextMenu, closeContextMenu } = useContextMenu();
  const [hasFocus, setHasFocus] = useState(false);

  const defaultRenderNode = useCallback(
    (row: NodeRow) => {
      const node = nodes.get(row.id);
      if (!node) return null;

      const state = context.services.store.getState();
      const enhancedRow = {
        ...row,
        node,
        selected: state.selectedNodes.has(row.id),
        expanded: state.expandedNodes.has(row.id),
        visible: true,
        isCursor: state.cursorId === row.id,
        isFocus: state.focusId === row.id,
      };

      return (
        <TreeNode
          key={row.id}
          row={enhancedRow}
          indentSize={indentSize}
          resolveIcon={resolveIcon}
        />
      );
    },
    [nodes, context.services.store, indentSize],
  );

  const defaultRenderEmptyState = () => (
    <div
      className={
        emptyStateClassName ||
        "text-center py-8 text-gray-500 dark:text-gray-400"
      }
    >
      No nodes to display
    </div>
  );

  const defaultRenderRootDropArea = (isDragOver: boolean) => (
    <div
      className={clsx(
        rootDropAreaClassName || "node-tree-root-drop-area",
        isDragOver && "drag-over",
      )}
      style={{
        flex: 1,
        minHeight: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s ease",
        cursor: "pointer",
      }}
    />
  );

  return (
    <>
      <div
        ref={context.elementRef}
        tabIndex={0}
        className={clsx(
          "node-tree-container",
          hasFocus && "has-focus",
          className,
        )}
        style={{
          ...style,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
      >
        <div className="node-tree-content" style={{ flexShrink: 0 }}>
          {indexedNodes && indexedNodes.length > 0
            ? indexedNodes.map((row: NodeRow) => {
                if (renderNode) {
                  const node = nodes.get(row.id);
                  if (!node) return null;

                  const state = context.services.store.getState();
                  const enhancedRow = {
                    ...row,
                    node,
                    selected: state.selectedNodes.has(row.id),
                    expanded: state.expandedNodes.has(row.id),
                    visible: true,
                    isCursor: state.cursorId === row.id,
                    isFocus: state.focusId === row.id,
                  };

                  return renderNode({ row: enhancedRow });
                }
                return defaultRenderNode(row);
              })
            : renderEmptyState
              ? renderEmptyState()
              : defaultRenderEmptyState()}
        </div>

        {/* Root Drop Area - sempre visível quando houver nodes e showRootDropArea for true */}
        {showRootDropArea && indexedNodes && indexedNodes.length > 0 && (
          <div
            {...context.handlers.rootDropProps}
            style={{ flex: 1, display: "flex" }}
          >
            {renderRootDropArea
              ? renderRootDropArea(dragOverId === "root")
              : defaultRenderRootDropArea(dragOverId === "root")}
          </div>
        )}
      </div>

      {showContextMenu && contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          nodeId={contextMenu.nodeId}
          containerElementRef={contextMenu.containerElementRef}
          onClose={closeContextMenu}
          className={contextMenuClassName}
          onAction={onContextMenuAction}
        />
      )}
    </>
  );
}
